import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Compass, MessageCircle } from "lucide-react";
import { IMAGES, MESSAGES, scrollToId, waLink } from "@/data/content";
import { MaskedLine } from "./Reveal";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-[100svh] items-end overflow-hidden bg-navy"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="واجهة معمارية حديثة وقت الغسق تمثل هوية شيادن الأصول العقارية"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/25" />
      <div className="absolute inset-0 bg-navy/25" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-navy via-navy/40 to-transparent" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-40 md:px-10 md:pb-28"
      >
        <MaskedLine delay={0.15}>
          <span
            data-testid="hero-wordmark"
            className="font-display text-[clamp(3.2rem,13vw,10.5rem)] font-bold leading-[0.95] tracking-[0.06em] text-cream"
          >
            SHIYADN
          </span>
        </MaskedLine>
        <MaskedLine delay={0.35}>
          <span className="mt-2 inline-block font-head text-lg md:text-2xl font-bold text-copper">
            شيادن الأصول العقارية
          </span>
        </MaskedLine>

        <motion.div
          initial={{ scaleX: 0, transformOrigin: "right" }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.3, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="rule-copper my-8 w-56 md:w-80"
        />

        <MaskedLine delay={0.55}>
          <h1
            data-testid="hero-tagline"
            className="max-w-3xl font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.25] text-cream"
          >
            رؤيتك العقارية تبدأ من أصل ثابت
          </h1>
        </MaskedLine>

        <motion.p
          data-testid="hero-description"
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-cream/85"
        >
          حلول احترافية في التسويق والوساطة والتأجير للغير، بخدمة واضحة
          واهتمام يجعل تجربتك العقارية أكثر سهولة.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={waLink(MESSAGES.general)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="group inline-flex items-center gap-3 rounded-full bg-copper px-8 py-4 font-head text-base font-bold text-navy transition-transform duration-300 hover:scale-[1.04]"
          >
            <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-12" />
            تواصل عبر واتساب
          </a>
          <button
            onClick={() => scrollToId("#services")}
            data-testid="hero-services-button"
            className="group inline-flex items-center gap-3 rounded-full border border-cream/40 px-8 py-4 font-head text-base font-bold text-cream transition-colors duration-300 hover:border-copper hover:text-copper"
          >
            <Compass className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
            اكتشف خدماتنا
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 md:block"
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="h-14 w-px bg-cream/40"
        />
      </motion.div>
    </section>
  );
}
